import React, { useState } from 'react';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import { Chart as ChartJS, ArcElement, Tooltip, Legend, CategoryScale, LinearScale, BarElement, Title } from 'chart.js';

// Register Chart.js components
ChartJS.register(ArcElement, Tooltip, Legend, CategoryScale, LinearScale, BarElement, Title);

const ProjectReportGenerator = ({ 
  layoutState, 
  buildingSettings,
  floorCount,
  hasPodium,
  calculateProjectMetrics,
  comprehensiveResults,
  unitQuantities,
  pcaBuildingData
}) => {
  const [isGenerating, setIsGenerating] = useState(false);
  const [generationProgress, setGenerationProgress] = useState(0);

  // Calculate all metrics for the report
  const getReportData = () => {
    const metrics = calculateProjectMetrics();
    const currentDate = new Date().toLocaleDateString();
    
    // Get building-specific data
    const buildingData = layoutState.buildPaths?.map(buildPath => {
      const buildingBoxes = layoutState.boxes?.filter(box => box.buildPathId === buildPath.id) || [];
      const settings = buildingSettings[buildPath.id] || {};
      const totalUnits = buildingBoxes.length * 2 * floorCount;
      
      return {
        id: buildPath.id,
        boxCount: buildingBoxes.length,
        totalUnits,
        unitTypeDistribution: settings.unitTypeDistribution || {},
        businessModelDistribution: settings.businessModelDistribution || {}
      };
    }) || [];

    // Calculate financial projections
    const componentArea = metrics.componentAreaAllFloors;
    const componentsMin = 135; // $/sq ft
    const componentsMax = 185; // $/sq ft
    const onSiteMin = 100; // $/sq ft
    const onSiteMax = 150; // $/sq ft
    
    const financials = {
      componentsCostMin: componentArea * componentsMin,
      componentsCostMax: componentArea * componentsMax,
      onSiteCostMin: componentArea * onSiteMin,
      onSiteCostMax: componentArea * onSiteMax
    };

    return {
      metrics,
      buildingData,
      financials,
      currentDate,
      projectName: 'ModIQ Project Analysis',
      comprehensiveResults,
      unitQuantities
    };
  };

  // Create unit mix pie chart
  const createUnitMixChart = async (unitQuantities) => {
    const canvas = document.createElement('canvas');
    canvas.width = 400;
    canvas.height = 300;
    const ctx = canvas.getContext('2d');

    // Filter out zero quantities
    const nonZeroUnits = Object.entries(unitQuantities).filter(([_, count]) => count > 0);
    
    if (nonZeroUnits.length === 0) {
      // No units to display
      ctx.fillStyle = '#f3f4f6';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      ctx.fillStyle = '#6b7280';
      ctx.font = '16px Arial';
      ctx.textAlign = 'center';
      ctx.fillText('No unit data available', canvas.width / 2, canvas.height / 2);
      return canvas.toDataURL();
    }

    const labels = nonZeroUnits.map(([type, _]) => {
      // Convert unit type keys to readable labels
      const labelMap = {
        studio: 'Studio',
        oneBR: '1 Bedroom',
        twoBR: '2 Bedroom',
        studioPlus: 'Studio+',
        oneBRPlus: '1 Bedroom+'
      };
      return labelMap[type] || type;
    });
    
    const data = nonZeroUnits.map(([_, count]) => count);
    const colors = ['#3b82f6', '#059669', '#dc2626', '#7c3aed', '#f59e0b'];
    
    // Simple pie chart implementation
    const total = data.reduce((sum, value) => sum + value, 0);
    let currentAngle = -Math.PI / 2; // Start at top
    
    const centerX = canvas.width / 2;
    const centerY = canvas.height / 2 - 20; // Leave space for legend
    const radius = Math.min(centerX, centerY) - 40;
    
    // Draw pie slices
    data.forEach((value, index) => {
      const sliceAngle = (value / total) * 2 * Math.PI;
      
      ctx.beginPath();
      ctx.moveTo(centerX, centerY);
      ctx.arc(centerX, centerY, radius, currentAngle, currentAngle + sliceAngle);
      ctx.closePath();
      ctx.fillStyle = colors[index % colors.length];
      ctx.fill();
      ctx.strokeStyle = '#ffffff';
      ctx.lineWidth = 2;
      ctx.stroke();
      
      // Add percentage labels on slices
      if (sliceAngle > 0.1) { // Only show label if slice is large enough
        const labelAngle = currentAngle + sliceAngle / 2;
        const labelX = centerX + Math.cos(labelAngle) * (radius * 0.7);
        const labelY = centerY + Math.sin(labelAngle) * (radius * 0.7);
        
        ctx.fillStyle = '#ffffff';
        ctx.font = 'bold 12px Arial';
        ctx.textAlign = 'center';
        ctx.fillText(`${((value / total) * 100).toFixed(1)}%`, labelX, labelY);
      }
      
      currentAngle += sliceAngle;
    });
    
    // Draw legend
    const legendY = canvas.height - 60;
    labels.forEach((label, index) => {
      const legendX = 20 + (index * 80);
      
      // Color box
      ctx.fillStyle = colors[index % colors.length];
      ctx.fillRect(legendX, legendY, 12, 12);
      
      // Label text
      ctx.fillStyle = '#374151';
      ctx.font = '10px Arial';
      ctx.textAlign = 'left';
      ctx.fillText(label, legendX + 16, legendY + 10);
      ctx.fillText(`(${data[index]})`, legendX + 16, legendY + 22);
    });
    
    // Title
    ctx.fillStyle = '#1f2937';
    ctx.font = 'bold 16px Arial';
    ctx.textAlign = 'center';
    ctx.fillText('Unit Mix Distribution', canvas.width / 2, 25);
    
    return canvas.toDataURL();
  };

  // Create cost breakdown chart
  const createCostChart = async (financials) => {
    const canvas = document.createElement('canvas');
    canvas.width = 400;
    canvas.height = 300;
    const ctx = canvas.getContext('2d');

    const categories = ['Components (Low)', 'Components (High)', 'On-Site (Low)', 'On-Site (High)'];
    const values = [
      financials.componentsCostMin / 1000000, // Convert to millions
      financials.componentsCostMax / 1000000,
      financials.onSiteCostMin / 1000000,
      financials.onSiteCostMax / 1000000
    ];
    const colors = ['#3b82f6', '#1d4ed8', '#059669', '#047857'];

    // Chart dimensions
    const chartX = 60;
    const chartY = 40;
    const chartWidth = canvas.width - 100;
    const chartHeight = canvas.height - 100;
    
    const maxValue = Math.max(...values);
    const barWidth = chartWidth / categories.length - 20;

    // Draw bars
    values.forEach((value, index) => {
      const barHeight = (value / maxValue) * chartHeight;
      const x = chartX + (index * (barWidth + 20));
      const y = chartY + chartHeight - barHeight;
      
      // Bar
      ctx.fillStyle = colors[index];
      ctx.fillRect(x, y, barWidth, barHeight);
      
      // Value label on top of bar
      ctx.fillStyle = '#374151';
      ctx.font = '10px Arial';
      ctx.textAlign = 'center';
      ctx.fillText(`$${value.toFixed(1)}M`, x + barWidth / 2, y - 5);
      
      // Category label
      ctx.save();
      ctx.translate(x + barWidth / 2, chartY + chartHeight + 15);
      ctx.rotate(-Math.PI / 6); // Rotate for better fit
      ctx.textAlign = 'center';
      ctx.font = '9px Arial';
      ctx.fillText(categories[index], 0, 0);
      ctx.restore();
    });

    // Y-axis
    ctx.strokeStyle = '#d1d5db';
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.moveTo(chartX, chartY);
    ctx.lineTo(chartX, chartY + chartHeight);
    ctx.stroke();

    // Y-axis labels
    for (let i = 0; i <= 5; i++) {
      const y = chartY + (i * chartHeight / 5);
      const value = maxValue * (5 - i) / 5;
      
      ctx.fillStyle = '#6b7280';
      ctx.font = '9px Arial';
      ctx.textAlign = 'right';
      ctx.fillText(`$${value.toFixed(1)}M`, chartX - 5, y + 3);
      
      // Grid line
      ctx.strokeStyle = '#f3f4f6';
      ctx.beginPath();
      ctx.moveTo(chartX, y);
      ctx.lineTo(chartX + chartWidth, y);
      ctx.stroke();
    }

    // Title
    ctx.fillStyle = '#1f2937';
    ctx.font = 'bold 16px Arial';
    ctx.textAlign = 'center';
    ctx.fillText('Project Cost Breakdown', canvas.width / 2, 25);

    return canvas.toDataURL();
  };

  // Create project metrics dashboard
  const createMetricsDashboard = async (metrics) => {
    const canvas = document.createElement('canvas');
    canvas.width = 500;
    canvas.height = 300;
    const ctx = canvas.getContext('2d');

    // Background
    ctx.fillStyle = '#f8fafc';
    ctx.fillRect(0, 0, canvas.width, canvas.height);

    // Title
    ctx.fillStyle = '#1f2937';
    ctx.font = 'bold 18px Arial';
    ctx.textAlign = 'center';
    ctx.fillText('Project Overview Dashboard', canvas.width / 2, 30);

    // Create metric cards
    const metricCards = [
      { label: 'Buildings', value: metrics.totalBuildings, color: '#059669', icon: '🏢' },
      { label: 'Total Units', value: metrics.totalUnits, color: '#3b82f6', icon: '🏠' },
      { label: 'Total Boxes', value: metrics.totalBoxesAllFloors, color: '#8b5cf6', icon: '📦' },
      { label: 'Land Efficiency', value: `${metrics.landEfficiency.toFixed(1)}%`, color: '#ef4444', icon: '📊' }
    ];

    const cardWidth = 100;
    const cardHeight = 80;
    const cardSpacing = 20;
    const startX = (canvas.width - (metricCards.length * cardWidth + (metricCards.length - 1) * cardSpacing)) / 2;

    metricCards.forEach((card, index) => {
      const x = startX + index * (cardWidth + cardSpacing);
      const y = 60;

      // Card background
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(x, y, cardWidth, cardHeight);
      ctx.strokeStyle = '#e2e8f0';
      ctx.lineWidth = 1;
      ctx.strokeRect(x, y, cardWidth, cardHeight);

      // Icon
      ctx.font = '20px Arial';
      ctx.textAlign = 'center';
      ctx.fillText(card.icon, x + cardWidth / 2, y + 25);

      // Value
      ctx.fillStyle = card.color;
      ctx.font = 'bold 16px Arial';
      ctx.fillText(card.value.toString(), x + cardWidth / 2, y + 45);

      // Label
      ctx.fillStyle = '#6b7280';
      ctx.font = '10px Arial';
      ctx.fillText(card.label, x + cardWidth / 2, y + 60);
    });

    // Area breakdown
    ctx.fillStyle = '#1f2937';
    ctx.font = 'bold 14px Arial';
    ctx.textAlign = 'left';
    ctx.fillText('Area Breakdown:', 50, 180);

    const areaData = [
      { label: 'Component Area', value: `${metrics.componentAreaAllFloors.toLocaleString()} sq ft`, color: '#3b82f6' },
      { label: 'Podium Area', value: `${metrics.podiumArea.toLocaleString()} sq ft`, color: '#059669' },
      { label: 'Total Building', value: `${metrics.totalBuildingArea.toLocaleString()} sq ft`, color: '#dc2626' }
    ];

    areaData.forEach((item, index) => {
      const y = 200 + index * 20;
      
      // Color indicator
      ctx.fillStyle = item.color;
      ctx.fillRect(50, y - 8, 12, 12);
      
      // Label and value
      ctx.fillStyle = '#374151';
      ctx.font = '12px Arial';
      ctx.fillText(`${item.label}: ${item.value}`, 70, y);
    });

    return canvas.toDataURL();
  };

  // Capture building visualization (if available)
  const captureBuildingVisualization = async () => {
    try {
      // Try to find the MapboxPCA visualization container
      const visualizationElement = document.querySelector('[class*="mapbox"]') || 
                                   document.querySelector('canvas') ||
                                   document.querySelector('[id*="map"]');
      
      if (visualizationElement) {
        const canvas = await html2canvas(visualizationElement, {
          width: 600,
          height: 400,
          scale: 1,
          useCORS: true,
          allowTaint: true
        });
        return canvas.toDataURL();
      }
    } catch (error) {
      console.log('Could not capture building visualization:', error);
    }
    
    // Fallback: create a simple building layout diagram
    const canvas = document.createElement('canvas');
    canvas.width = 600;
    canvas.height = 400;
    const ctx = canvas.getContext('2d');
    
    // Background
    ctx.fillStyle = '#f8fafc';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    
    // Title
    ctx.fillStyle = '#1f2937';
    ctx.font = 'bold 16px Arial';
    ctx.textAlign = 'center';
    ctx.fillText('Building Layout Schematic', canvas.width / 2, 30);
    
    // Draw simplified building layout
    // Ensure buildPaths is an array
    const safeBuildPaths = Array.isArray(layoutState?.buildPaths) ? layoutState.buildPaths : [];
    if (safeBuildPaths.length > 0) {
      const padding = 50;
      const maxWidth = canvas.width - 2 * padding;
      const maxHeight = canvas.height - 100;
      
      safeBuildPaths.forEach((buildPath, index) => {
        const buildingBoxes = layoutState.boxes?.filter(box => box.buildPathId === buildPath.id) || [];
        
        if (buildingBoxes.length > 0) {
          // Calculate building bounds
          const minX = Math.min(...buildingBoxes.map(box => box.x));
          const maxX = Math.max(...buildingBoxes.map(box => box.x + box.width));
          const minY = Math.min(...buildingBoxes.map(box => box.y));
          const maxY = Math.max(...buildingBoxes.map(box => box.y + box.height));
          
          const buildingWidth = maxX - minX;
          const buildingHeight = maxY - minY;
          
          // Scale to fit canvas
          const scale = Math.min(maxWidth / buildingWidth, maxHeight / buildingHeight) * 0.8;
          
          const offsetX = padding + (maxWidth - buildingWidth * scale) / 2;
          const offsetY = 60 + (maxHeight - buildingHeight * scale) / 2;
          
          // Draw building outline
          ctx.strokeStyle = '#374151';
          ctx.lineWidth = 2;
          ctx.strokeRect(
            offsetX + (minX * scale),
            offsetY + (minY * scale),
            buildingWidth * scale,
            buildingHeight * scale
          );
          
          // Draw boxes
          buildingBoxes.forEach(box => {
            const x = offsetX + ((box.x - minX) * scale);
            const y = offsetY + ((box.y - minY) * scale);
            const width = box.width * scale;
            const height = box.height * scale;
            
            ctx.fillStyle = '#3b82f6';
            ctx.fillRect(x, y, width, height);
            ctx.strokeStyle = '#ffffff';
            ctx.lineWidth = 1;
            ctx.strokeRect(x, y, width, height);
          });
          
          // Building label
          ctx.fillStyle = '#1f2937';
          ctx.font = '12px Arial';
          ctx.textAlign = 'center';
          ctx.fillText(
            `Building ${buildPath.id} (${buildingBoxes.length} boxes)`,
            offsetX + (buildingWidth * scale) / 2,
            offsetY + (buildingHeight * scale) + 20
          );
        }
      });
    } else {
      // No building data
      ctx.fillStyle = '#6b7280';
      ctx.font = '14px Arial';
      ctx.textAlign = 'center';
      ctx.fillText('No building layout data available', canvas.width / 2, canvas.height / 2);
    }
    
    return canvas.toDataURL();
  };

  // Format currency for display
  const formatCurrency = (amount) => {
    if (amount >= 1000000) {
      return `$${(amount / 1000000).toFixed(1)}M`;
    } else if (amount >= 1000) {
      return `$${(amount / 1000).toFixed(0)}K`;
    } else {
      return `$${amount.toLocaleString()}`;
    }
  };

  // Generate PDF report with visuals
  const generatePDFReport = async () => {
    setIsGenerating(true);
    setGenerationProgress(0);

    try {
      const reportData = getReportData();
      const pdf = new jsPDF('p', 'mm', 'a4');
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      const margin = 20;
      const contentWidth = pageWidth - 2 * margin;
      
      let yPosition = margin;
      
      // Helper function to add new page if needed
      const checkPageBreak = (requiredHeight) => {
        if (yPosition + requiredHeight > pageHeight - margin) {
          pdf.addPage();
          yPosition = margin;
          return true;
        }
        return false;
      };

      // Helper function to add text with word wrap
      const addText = (text, x, y, options = {}) => {
        const fontSize = options.fontSize || 10;
        const fontStyle = options.fontStyle || 'normal';
        const maxWidth = options.maxWidth || contentWidth;
        
        pdf.setFontSize(fontSize);
        pdf.setFont('helvetica', fontStyle);
        
        if (options.color) {
          pdf.setTextColor(...options.color);
        } else {
          pdf.setTextColor(0, 0, 0);
        }
        
        const lines = pdf.splitTextToSize(text, maxWidth);
        lines.forEach((line, index) => {
          pdf.text(line, x, y + (index * fontSize * 0.4));
        });
        
        return lines.length * fontSize * 0.4;
      };

      // Helper function to add image
      const addImage = (dataUrl, x, y, width, height) => {
        if (dataUrl) {
          pdf.addImage(dataUrl, 'PNG', x, y, width, height);
        }
      };

      setGenerationProgress(10);

      // Generate visual elements
      console.log('Generating charts and visuals...');
      const unitMixChart = await createUnitMixChart(reportData.unitQuantities || {});
      const costChart = await createCostChart(reportData.financials);
      const metricsDashboard = await createMetricsDashboard(reportData.metrics);
      const buildingVisualization = await captureBuildingVisualization();

      setGenerationProgress(25);

      // ===== PAGE 1: COVER PAGE WITH DASHBOARD =====
      pdf.setFontSize(24);
      pdf.setFont('helvetica', 'bold');
      pdf.setTextColor(46, 125, 246); // Blue color
      pdf.text('ModIQ Project Report', pageWidth / 2, 40, { align: 'center' });
      
      pdf.setFontSize(16);
      pdf.setFont('helvetica', 'normal');
      pdf.setTextColor(0, 0, 0);
      pdf.text(reportData.projectName, pageWidth / 2, 55, { align: 'center' });
      
      pdf.setFontSize(12);
      pdf.text(`Generated on ${reportData.currentDate}`, pageWidth / 2, 70, { align: 'center' });
      
      // Add metrics dashboard
      if (metricsDashboard) {
        addImage(metricsDashboard, margin, 80, contentWidth, contentWidth * 0.6);
      }
      
      // Summary statistics at bottom
      pdf.setFontSize(11);
      pdf.setFont('helvetica', 'normal');
      let summaryY = 200;
      pdf.text(`📊 ${reportData.metrics.totalBuildings} Buildings • ${reportData.metrics.totalUnits} Units • ${reportData.metrics.totalBoxesAllFloors} Boxes`, pageWidth / 2, summaryY, { align: 'center' });
      pdf.text(`💰 Cost Range: ${formatCurrency(reportData.financials.componentsCostMin)} - ${formatCurrency(reportData.financials.componentsCostMax)}`, pageWidth / 2, summaryY + 10, { align: 'center' });

      setGenerationProgress(40);

      // ===== PAGE 2: BUILDING LAYOUT =====
      pdf.addPage();
      yPosition = margin;
      
      // Header
      pdf.setFontSize(18);
      pdf.setFont('helvetica', 'bold');
      pdf.setTextColor(46, 125, 246);
      yPosition += addText('Building Layout & Visualization', margin, yPosition, { fontSize: 18, fontStyle: 'bold', color: [46, 125, 246] });
      yPosition += 10;
      
      // Add building visualization
      if (buildingVisualization) {
        addImage(buildingVisualization, margin, yPosition, contentWidth, contentWidth * 0.67);
        yPosition += contentWidth * 0.67 + 10;
      }

      // Building summary table
      checkPageBreak(60);
      pdf.setFontSize(14);
      pdf.setFont('helvetica', 'bold');
      yPosition += addText('Building Summary', margin, yPosition, { fontSize: 14, fontStyle: 'bold' });
      yPosition += 5;
      
      const buildingSummaryData = [
        ['Building ID', 'Boxes', 'Units', 'Area (sq ft)'],
        ...reportData.buildingData.map(building => [
          building.id.toString(),
          building.boxCount.toString(),
          building.totalUnits.toString(),
          (building.boxCount * 755).toLocaleString() // Approximate area per box
        ])
      ];
      
      // Draw building summary table
      const rowHeight = 7;
      const colWidth = contentWidth / 4;
      
      buildingSummaryData.forEach((row, index) => {
        const isHeader = index === 0;
        const rowY = yPosition + (index * rowHeight);
        
        pdf.setFont('helvetica', isHeader ? 'bold' : 'normal');
        pdf.setFillColor(isHeader ? 240 : 250, isHeader ? 240 : 250, isHeader ? 240 : 252);
        
        row.forEach((cell, cellIndex) => {
          const cellX = margin + (cellIndex * colWidth);
          pdf.rect(cellX, rowY, colWidth, rowHeight, 'FD');
          pdf.text(cell, cellX + 3, rowY + 5);
        });
      });

      setGenerationProgress(55);

      // ===== PAGE 3: UNIT MIX & FINANCIAL ANALYSIS =====
      pdf.addPage();
      yPosition = margin;
      
      pdf.setFontSize(18);
      pdf.setFont('helvetica', 'bold');
      pdf.setTextColor(46, 125, 246);
      yPosition += addText('Unit Mix & Financial Analysis', margin, yPosition, { fontSize: 18, fontStyle: 'bold', color: [46, 125, 246] });
      yPosition += 10;
      
      // Unit Mix Chart
      if (unitMixChart) {
        pdf.setFontSize(14);
        pdf.setFont('helvetica', 'bold');
        yPosition += addText('Unit Distribution', margin, yPosition, { fontSize: 14, fontStyle: 'bold' });
        yPosition += 5;
        
        addImage(unitMixChart, margin, yPosition, contentWidth * 0.6, contentWidth * 0.45);
        yPosition += contentWidth * 0.45 + 15;
      }
      
      // Cost Breakdown Chart
      if (costChart) {
        checkPageBreak(80);
        pdf.setFontSize(14);
        pdf.setFont('helvetica', 'bold');
        yPosition += addText('Cost Analysis', margin, yPosition, { fontSize: 14, fontStyle: 'bold' });
        yPosition += 5;
        
        addImage(costChart, margin, yPosition, contentWidth * 0.6, contentWidth * 0.45);
        yPosition += contentWidth * 0.45 + 10;
      }

      setGenerationProgress(70);

      // ===== PAGE 4: PROJECT METRICS =====
      pdf.addPage();
      yPosition = margin;
      
      pdf.setFontSize(18);
      pdf.setFont('helvetica', 'bold');
      pdf.setTextColor(46, 125, 246);
      yPosition += addText('Project Metrics & Performance', margin, yPosition, { fontSize: 18, fontStyle: 'bold', color: [46, 125, 246] });
      yPosition += 10;
      
      // Detailed metrics table
      const metricsData = [
        ['Metric', 'Value', 'Details'],
        ['Total Buildings', reportData.metrics.totalBuildings.toString(), 'Physical structures'],
        ['Total Floors', reportData.metrics.floorCount.toString(), 'Stories per building'],
        ['Has Podium', reportData.metrics.hasPodium ? 'Yes' : 'No', 'Ground level platform'],
        ['Total Units', reportData.metrics.totalUnits.toString(), 'Livable spaces'],
        ['Total Boxes', reportData.metrics.totalBoxesAllFloors.toString(), 'Modular components'],
        ['Component Area', `${reportData.metrics.componentAreaAllFloors.toLocaleString()} sq ft`, 'Manufactured space'],
        ['Project Total', `${reportData.metrics.projectTotal.toLocaleString()} sq ft`, 'Total built area'],
        ['Land Efficiency', `${reportData.metrics.landEfficiency.toFixed(1)}%`, 'Site utilization']
      ];
      
      // Draw metrics table
      const metColWidths = [contentWidth * 0.3, contentWidth * 0.25, contentWidth * 0.45];
      
      metricsData.forEach((row, index) => {
        const isHeader = index === 0;
        const rowY = yPosition + (index * rowHeight);
        
        pdf.setFont('helvetica', isHeader ? 'bold' : 'normal');
        pdf.setFillColor(isHeader ? 240 : 250, isHeader ? 240 : 250, isHeader ? 240 : 252);
        
        let currentX = margin;
        row.forEach((cell, cellIndex) => {
          const cellWidth = metColWidths[cellIndex];
          pdf.rect(currentX, rowY, cellWidth, rowHeight, 'FD');
          pdf.text(cell, currentX + 3, rowY + 5);
          currentX += cellWidth;
        });
      });

      setGenerationProgress(85);

      // ===== ModIQ Results (if available) =====
      if (reportData.comprehensiveResults) {
        pdf.addPage();
        yPosition = margin;
        
        pdf.setFontSize(18);
        pdf.setFont('helvetica', 'bold');
        pdf.setTextColor(46, 125, 246);
        yPosition += addText('ModIQ Pro Optimization Results', margin, yPosition, { fontSize: 18, fontStyle: 'bold', color: [46, 125, 246] });
        yPosition += 10;
        
        const winner = reportData.comprehensiveResults.summary.winner;
        if (winner) {
          // Winner highlights
          pdf.setDrawColor(34, 197, 94);
          pdf.setFillColor(240, 253, 244);
          pdf.roundedRect(margin, yPosition, contentWidth, 40, 5, 5, 'FD');
          
          pdf.setFontSize(14);
          pdf.setFont('helvetica', 'bold');
          pdf.setTextColor(22, 163, 74);
          yPosition += addText('🏆 Optimal Configuration Found', margin + 10, yPosition + 10, { fontSize: 14, fontStyle: 'bold', color: [22, 163, 74] });
          
          pdf.setFontSize(11);
          pdf.setFont('helvetica', 'normal');
          pdf.setTextColor(0, 0, 0);
          yPosition += addText(`${winner.metrics.boxCount} boxes • ${winner.metrics.buildingCount} buildings • ${winner.angle.toFixed(1)}° rotation • ${winner.strategyName}`, margin + 10, yPosition + 5);
          yPosition += addText(`Total Units: ${winner.metrics.unitsTotal} • Optimization Time: ${(reportData.comprehensiveResults.summary.totalTimeMs / 1000).toFixed(1)}s`, margin + 10, yPosition + 5);
          
          yPosition += 50;
        }
      }

      setGenerationProgress(95);

      // ===== FOOTER ON EACH PAGE =====
      const totalPages = pdf.internal.getNumberOfPages();
      for (let i = 1; i <= totalPages; i++) {
        pdf.setPage(i);
        pdf.setFontSize(8);
        pdf.setFont('helvetica', 'normal');
        pdf.setTextColor(128, 128, 128);
        pdf.text(`ModIQ Project Report - Page ${i} of ${totalPages}`, pageWidth / 2, pageHeight - 10, { align: 'center' });
        pdf.text(`Generated ${reportData.currentDate}`, pageWidth - margin, pageHeight - 10, { align: 'right' });
      }

      setGenerationProgress(100);

      // Save the PDF
      const fileName = `ModIQ_Visual_Report_${new Date().toISOString().slice(0, 10)}.pdf`;
      pdf.save(fileName);
      
      setTimeout(() => {
        setIsGenerating(false);
        setGenerationProgress(0);
      }, 1000);

    } catch (error) {
      console.error('Error generating visual PDF report:', error);
      alert('Error generating PDF report. Please try again.');
      setIsGenerating(false);
      setGenerationProgress(0);
    }
  };

  return (
    <div style={{
      padding: '1rem',
      backgroundColor: '#f8fafc',
      borderRadius: '8px',
      border: '1px solid #e2e8f0'
    }}>
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '1rem'
      }}>
        <div>
          <h3 style={{
            fontSize: '1.1rem',
            fontWeight: '600',
            color: '#374151',
            margin: '0 0 0.5rem 0'
          }}>
            Visual Project Report Generator
          </h3>
          <p style={{
            fontSize: '0.875rem',
            color: '#6b7280',
            margin: 0
          }}>
            Generate a comprehensive PDF report with charts, visualizations, and project analysis.
          </p>
        </div>
        
        <button
          onClick={generatePDFReport}
          disabled={isGenerating || !layoutState.buildPaths?.length}
          style={{
            padding: '10px 20px',
            backgroundColor: isGenerating ? '#9ca3af' : '#dc2626',
            color: 'white',
            border: 'none',
            borderRadius: '6px',
            cursor: isGenerating ? 'not-allowed' : 'pointer',
            fontSize: '0.875rem',
            fontWeight: '500',
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            minWidth: '140px',
            justifyContent: 'center'
          }}
        >
          {isGenerating ? (
            <>
              <div style={{
                width: '16px',
                height: '16px',
                border: '2px solid #ffffff40',
                borderTop: '2px solid #ffffff',
                borderRadius: '50%',
                animation: 'spin 1s linear infinite'
              }} />
              Generating...
            </>
          ) : (
            <>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                <polyline points="14,2 14,8 20,8"/>
                <path d="M16 13H8M16 17H8M10 9H8"/>
              </svg>
              Generate Visual PDF
            </>
          )}
        </button>
      </div>
      
      {/* Enhanced Progress bar with stage information */}
      {isGenerating && (
        <div style={{
          marginTop: '1rem',
          backgroundColor: '#e5e7eb',
          borderRadius: '8px',
          overflow: 'hidden'
        }}>
          <div
            style={{
              height: '8px',
              backgroundColor: '#dc2626',
              width: `${generationProgress}%`,
              transition: 'width 0.3s ease',
              borderRadius: '8px'
            }}
          />
          <div style={{
            textAlign: 'center',
            fontSize: '0.75rem',
            color: '#6b7280',
            marginTop: '4px'
          }}>
            {generationProgress < 25 && 'Creating charts and visualizations...'}
            {generationProgress >= 25 && generationProgress < 55 && 'Generating building layouts...'}
            {generationProgress >= 55 && generationProgress < 85 && 'Processing unit mix and financial data...'}
            {generationProgress >= 85 && 'Finalizing PDF report...'}
            <div style={{ fontSize: '0.7rem', marginTop: '2px' }}>
              {generationProgress}% Complete
            </div>
          </div>
        </div>
      )}
      
      {!layoutState.buildPaths?.length && (
        <div style={{
          marginTop: '1rem',
          padding: '0.75rem',
          backgroundColor: '#fef3c7',
          borderRadius: '6px',
          fontSize: '0.875rem',
          color: '#92400e'
        }}>
          Please create a building layout first to generate a report.
        </div>
      )}

      <style jsx>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
};

export default ProjectReportGenerator; 